import * as ko from "knockout";
import {
    AVAILABLE_NUM_SEARCH_RESULTS,
    DEFAULT_CSV_DELIMITERS,
    DEFAULT_SEARCH_COLUMNS,
    LATEST_OBJECTS_AUTOMATIC,
    LATEST_OBJECTS_SHOW_LIMIT,
    LATEST_OBJECTS_STORE_LIMIT,
    LAYOUT_LEFT_TOGGLE,
    LAYOUT_RIGHT_TOGGLE,
    ObjectTypeId,
    RESULTS_PER_PAGE
} from "../constants";
import {_} from 'lodash';
import {global} from "../appContext";
import {nsAmplify} from "./nsAmplify";

/**
 * Store for keeping values between sessions.
 */

type TStoreType = 'user' | 'superadmin' | 'developer';

type IStore = Partial<Record<TStoreId, any>>;

export type TStoreId = 'all.organizations'
    | 'availableforms.show.all'
    | 'css.block.fullwidth'
    | 'csv.delimiter'
    | 'details.element.toggle'
    | 'details.preview.toggle'
    | 'details.settings.toggle'
    | 'eadm.expressionbuilder.ehub'
    | 'formtemplatesearch.show.all'
    | 'hide.automatic.override.warning'
    | 'history.filters.errors'
    | 'history.toggle.comment'
    | 'history.toggle.derived'
    | 'language' // the logged in user's preferred language
    | 'latest.objects.automatic'
    | 'latest.objects.show.limit'
    | 'latest.objects.store.limit'
    | 'latest.objects.store.list'
    | 'login.remember.organization'
    | 'layout.left.toggle'
    | 'layout.right.mode'
    | 'layout.right.toggle'
    | 'messageflowsearch.show.all'
    | 'num.ruleset.derived.values'
    | 'num.search.results'
    | 'prefix.translation'
    | 'production.warning'
    | 'report.filter.errors'
    | 'report.sort.column'
    | 'report.sort.direction'
    | 'ruleset.toggle.derived'
    | 'selected.company.login'
    | 'should.use.console'
    | 'show.component.name'
    | 'show.source.translations'
    | 'show.super.adm.emulating'
    | 'show.translation.key'
    | 'sidepanel.section.admintools'
    | 'sidepanel.section.apiuser'
    | 'sidepanel.section.columns'
    | 'sidepanel.section.department'
    | 'sidepanel.section.general'
    | 'sidepanel.section.group'
    | 'sidepanel.section.user'
    | 'syncfields.config.importformat'
    | 'syncfields.config.language'
    | 'syncfields.config.objecttypeid'
    | 'syncfields.crud.asc'
    | 'syncfields.crud.searchkey'
    | 'syncfields.crud.sortcol'
    | 'syncfields.show.all'
    | 'syncfields.show.derived'
    | 'syncfields.show.override'
    | 'syncfields.sort.asc'
    | 'syncfields.sort.col'
    | 'syncfields.toggle.derived'
    | 'syncfields.toggle.mandatory'
    | 'syncfields.toggle.override'
    | 'syncfields.toggle.show.all'
    | 'synctemplatesearch.show.all'
    | 'systemaccesssearch.collection.show.all'
    | 'systemaccesssearch.role.show.all'
    | 'toggle.customer.apiaccess'
    | 'translation.config.pagenr'
    | 'translation.list.pagenr'
    | 'use.eadm.expressionbuilder'
    | 'user.toggle.employment'
    | 'user.toggle.exportqueue'
    | 'user.toggle.formdata'
    | 'user.toggle.membership'
    | 'user.toggle.personal'
    | 'user.toggle.positions'
    | 'user.toggle.tasksandmessages'
    | 'user.toggle.userhistory'
    | 'user.toggle.userpermissions'
    | `search.object.syncfields${ObjectTypeId}`
    | `selected-list-${ObjectTypeId}`

const StoreUser: IStore = {
    'all.organizations': false,
    'availableforms.show.all': true,
    'css.block.fullwidth': true,
    'csv.delimiter': DEFAULT_CSV_DELIMITERS,
    'details.element.toggle': null,
    'details.preview.toggle': null,
    'details.settings.toggle': null,
    'eadm.expressionbuilder.ehub': false,
    'formtemplatesearch.show.all': true,
    'hide.automatic.override.warning': false,
    'history.filters.errors': false,
    'history.toggle.comment': true,
    'history.toggle.derived': false,
    'latest.objects.automatic': LATEST_OBJECTS_AUTOMATIC,
    'latest.objects.show.limit': LATEST_OBJECTS_SHOW_LIMIT,
    'latest.objects.store.limit': LATEST_OBJECTS_STORE_LIMIT,
    'latest.objects.store.list': [],
    'login.remember.organization': true,
    'layout.left.toggle': LAYOUT_LEFT_TOGGLE,
    'layout.right.mode': 'profile',
    'layout.right.toggle': LAYOUT_RIGHT_TOGGLE,
    'messageflowsearch.show.all': true,
    'num.ruleset.derived.values': 5,
    'num.search.results': RESULTS_PER_PAGE,
    'prefix.translation': false,
    'production.warning': false,
    'report.filter.errors': false,
    'report.sort.column': '$timestamp',
    'report.sort.direction': false,
    'should.use.console': false,
    'show.component.name': false,
    'show.source.translations': true,
    'show.super.adm.emulating': true,
    'show.translation.key': false,
    'sidepanel.section.admintools': false,
    'sidepanel.section.apiuser': false,
    'sidepanel.section.columns': false,
    'sidepanel.section.department': false,
    'sidepanel.section.general': false,
    'sidepanel.section.group': false,
    'sidepanel.section.user': false,
    'syncfields.crud.asc': true,
    'syncfields.crud.searchkey': '',
    'syncfields.crud.sortcol': 'name',
    'syncfields.show.all': false,
    'syncfields.show.derived': true,
    'syncfields.show.override': false,
    'synctemplatesearch.show.all': true,
    'systemaccesssearch.collection.show.all': true,
    'systemaccesssearch.role.show.all': true,
    'translation.config.pagenr': 0,
    'translation.list.pagenr': 0,
    'use.eadm.expressionbuilder': false,
    [`search.object.syncfields${ObjectTypeId.Department}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.Department],
    [`search.object.syncfields${ObjectTypeId.ExternalContact}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.ExternalContact],
    [`search.object.syncfields${ObjectTypeId.FormData}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.FormData],
    [`search.object.syncfields${ObjectTypeId.Group}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.Group],
    [`search.object.syncfields${ObjectTypeId.LegalUnit}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.LegalUnit],
    [`search.object.syncfields${ObjectTypeId.User}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.User],
    [`search.object.syncfields${ObjectTypeId.ApiUser}`]: DEFAULT_SEARCH_COLUMNS[ObjectTypeId.ApiUser],
};

// this is used until the user is logged in
const TemporaryStore = _.cloneDeep(StoreUser)

const _StoreSuperAdmin: Partial<Record<TStoreId, any>> = {
    'show.source.translations': false,
}
const StoreSuperAdmin: IStore = _.defaults(_StoreSuperAdmin, StoreUser);

const _StoreSuperDeveloper: Partial<Record<TStoreId, any>> = {
    'production.warning': true,
    'should.use.console': true,
}

const StoreSuperDeveloper: IStore = _.defaults(_StoreSuperDeveloper, StoreSuperAdmin);

const observables: Partial<{ [Key in TStoreId]: KnockoutObservable<any> }> = {};
const observablesArray: Partial<{ [Key in TStoreId]: KnockoutObservableArray<any> }> = {};

export function initStore() {
    const store = getStore();
    (Object.keys(store) as TStoreId[]).forEach((storeId) => {
        const savedVal = nsAmplify.store(storeId);
        if (savedVal !== undefined) {
            // if there is a saved value we'll use that
            storeObservable(storeId)(savedVal);
        } else {
            const storeVal = store[storeId];
            if (storeVal !== undefined) {
                // if the new store has a value we'll use that
                storeObservable(storeId)(storeVal);
            } else {
                // otherwise fall back to whatever the temporary store had
                const tempVal = TemporaryStore[storeId];
                storeObservable(storeId)(tempVal);
            }
        }
    });
}

// some store ids are concatenated strings, made on-the-fly - this function "converts" these to store observables
export function customStoreObservable(storeId: string, defaultValue?: any): KnockoutObservable<any> {
    return storeObservable(storeId as TStoreId, defaultValue)
}

export function storeObservable(storeId: TStoreId, defaultValue?: any): KnockoutObservable<any> {
    const store = getStore();
    if (Array.isArray(store[storeId]) || Array.isArray(defaultValue)) {
        return storeObservableArray(storeId, defaultValue);
    }
    if (storeId in observables) {
        return observables[storeId]!
    }
    defaultValue = getDefault(storeId, defaultValue)
    const obs = ko.observable(defaultValue)
    observables[storeId] = obs
    obs.subscribe((val: any) => nsAmplify.store(storeId, val))
    return obs
}

export function storeObservableArray(storeId: TStoreId, defaultValue?: any): KnockoutObservableArray<any> {
    if (storeId in observablesArray) {
        return observablesArray[storeId]!
    }
    defaultValue = getDefault(storeId, defaultValue)
    const obs = ko.observableArray(Array.isArray(defaultValue) ? defaultValue: [])
    observablesArray[storeId] = obs
    obs.subscribe((val: any) => nsAmplify.store(storeId, val))
    return obs
}

export function reInitStore() {
    const store = getStore();
    (Object.keys(store) as TStoreId[]).forEach(reInitStoreId);
}

export function reInitStoreId(storeId: TStoreId) {
    const store = getStore();
    if (storeId in observables) {
        observables[storeId]?.(_.cloneDeep(store[storeId]));
    }
    if (storeId in observablesArray) {
        observablesArray[storeId]?.(_.cloneDeep(store[storeId]));
    }
}

export function getStoreDefaultValue(storeId: TStoreId) {
    const store = getStore();
    return store[storeId];
}

function getStore(): IStore {
    if (!global?.loginManager || !global?.login?.isLoggedIn()) {
        return TemporaryStore
    }
    if (global.loginManager.isUserSA()) {
        if (global.loginManager.isUserDev()) {
            








            return StoreSuperDeveloper;
        }
        return StoreSuperAdmin;
    }
    return StoreUser;
}

function getDefault(storeId: TStoreId, defaultValue?: any) {
    const store = getStore();
    const storedValue = nsAmplify.store(storeId);
    defaultValue = defaultValue !== undefined ? defaultValue : store[storeId];
    const value = storedValue !== undefined ? storedValue : defaultValue;
    return _.cloneDeep(value);
}
